/**
 * The Global Audit Log — Step 19's page, digitaladmin's GlobalAuditPage
 * conventions ported: ENTITY_LABELS + buildDescription so every entry reads as
 * a sentence without querying anything; the expanded row renders ChangeEntry[]
 * as a strikethrough-from → to table (added=emerald, removed=red,
 * changed=amber); changesLabel() summarises — NEVER a raw payload dump.
 * Every new entity type MUST add a label + description case here in the same
 * commit as its writeAudit calls (the standard, verbatim).
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, ScrollText, X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { fullTime, relativeTime } from '../../lib/time';
import { useDebouncedUrlState, useUrlPatch, useUrlState } from '../../lib/urlState';
import { qk } from '../../lib/queryClient';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';

type ChangeEntry = {
  label: string;
  action?: 'added' | 'removed' | 'changed';
  from?: string | null;
  to?: string | null;
};

interface AuditEntry {
  id: string;
  user_name: string | null;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  action: string;
  payload: { name?: string; changes?: ChangeEntry[] | string | null; via?: string } | null;
  ip_address: string | null;
  created_at: number;
}

export const ENTITY_LABELS: Record<string, string> = {
  ticket: 'Ticket',
  session: 'Session',
  organisation: 'Organisation',
  user: 'User',
  role: 'Role',
  group: 'Group',
  // Step 104 — the desk's own lists. `department` was missing since Step 55.
  department: 'Department',
  location: 'Location',
  'requester-group': 'Requester group',
  'saved-view': 'Saved view',
  'canned-response': 'Canned response',
  'field-definition': 'Field',
  'form-layout': 'Form layout',
  'catalogue-category': 'Catalogue category',
  'catalogue-item': 'Catalogue item',
  'freeze-window': 'Freeze window',
  'approval-board': 'Approval board',
  'mail-settings': 'Mail settings',
  'sso-settings': 'Sign-in (SSO) settings',
  'app-settings': 'Branding',
  email: 'Email',
};

const ACTION_VERBS: Record<string, string> = {
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
  archived: 'archived',
  restored: 'restored',
  replied: 'replied on',
  noted: 'added a private note to',
  login: 'signed in',
  logout: 'signed out',
  merged: 'merged',
  linked: 'linked',
  unlinked: 'unlinked',
  held: 'put on hold',
  hold_expired: 'reopened (hold expired) on',
  hold_expired_late: 'reopened (hold expired, caught by the nightly integrity sweep) on',
  provision_started: 'started provisioning',
  provisioned: 'finished provisioning',
  provision_failed: 'failed to provision',
  activated: 'activated',
  deactivated: 'deactivated',
  sent: 'sent',
  send_failed: 'failed to send',
  discarded: 'discarded',
  field_index_resynced: 're-synced the drifted custom-field index for',
};

function buildDescription(e: AuditEntry): string {
  const who = e.user_name ?? 'Someone';
  if (e.entity_type === 'session') {
    return `${who} ${ACTION_VERBS[e.action] ?? e.action}${e.payload?.via ? ` (${e.payload.via})` : ''}`;
  }
  const label = ENTITY_LABELS[e.entity_type] ?? e.entity_type;
  const name = e.entity_name ?? e.payload?.name ?? '';
  return `${who} ${ACTION_VERBS[e.action] ?? e.action} ${label.toLowerCase()}${name ? ` “${name}”` : ''}`;
}

/** Summarise changes for the collapsed row — the standard's changesLabel(). */
function changesLabel(
  changes: AuditEntry['payload'] extends infer P ? (P extends { changes?: infer C } ? C : never) : never,
): string | null {
  if (!changes) return null;
  if (typeof changes === 'string') return changes;
  if (Array.isArray(changes)) return changes.map((c) => c.label).join(', ');
  return null;
}

/** A native date input's value (local YYYY-MM-DD) at local midnight, ms. */
function dayStart(v: string): number | null {
  if (!v) return null;
  const d = new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}
/** Same day, but the last millisecond of it — an inclusive "to" bound. */
function dayEnd(v: string): number | null {
  const start = dayStart(v);
  return start === null ? null : start + 24 * 60 * 60 * 1000 - 1;
}

export function AuditLogPage() {
  const [entityType, setEntityType] = useUrlState('entity', '');
  const [search, setSearch] = useDebouncedUrlState('q');
  const [fromDate, setFromDate] = useUrlState('from', '');
  const [toDate, setToDate] = useUrlState('to', '');
  const [page, setPage] = useUrlState('page', '1');
  // Which audit entry is expanded is exactly the thing you send a colleague:
  // "look at this change". In useState the link goes to an unexpanded list.
  const [expanded, setExpanded] = useUrlState('entry', null);
  const patchUrl = useUrlPatch();

  const activeFilterCount = [!!entityType, !!search, !!fromDate, !!toDate].filter(Boolean).length;

  const params = new URLSearchParams({ limit: '50', page });
  if (entityType) params.set('entityType', entityType);
  if (search) params.set('q', search);
  const from = dayStart(fromDate);
  if (from !== null) params.set('from', String(from));
  const to = dayEnd(toDate);
  if (to !== null) params.set('to', String(to));
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.audit(params.toString()),
    queryFn: () =>
      apiFetch<{ entries: AuditEntry[]; total: number; page: number; pages: number }>(
        `/api/admin/audit?${params}`,
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-center gap-3">
        <ScrollText className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Audit log</h1>
        {data && <span className="text-xs text-muted-foreground">{data.total} entries</span>}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage('1');
          }}
          placeholder="Search by actor or entity…"
          className="w-56"
        />
        <Select
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value);
            setPage('1');
          }}
          className="w-44"
        >
          <option value="">All entities</option>
          {Object.entries(ENTITY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
        {/* Step 113 — the same filters, as a file. */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => window.location.assign(`/api/admin/audit?${params.toString()}&format=csv`)}
          title="Download the log as it is filtered now"
        >
          <Download /> Export CSV
        </Button>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage('1');
          }}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm dark:[color-scheme:dark]"
          aria-label="From date"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage('1');
          }}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm dark:[color-scheme:dark]"
          aria-label="To date"
        />
        {activeFilterCount > 0 && (
          <button
            onClick={() => {
              patchUrl({ entity: null, q: null, from: null, to: null, page: null });
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      {isError && (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Could not load the audit log</p>
          <p className="text-xs text-destructive/80">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="text-xs text-destructive/80 underline hover:text-destructive"
          >
            Retry
          </button>
        </div>
      )}
      {isPending &&
        Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="mb-2 h-10 w-full" />)}

      <div className="divide-y rounded-lg border bg-card">
        {data?.entries.map((e) => {
          const changes = e.payload?.changes;
          const summary = changesLabel(changes as never);
          const isOpen = expanded === e.id;
          const expandable = Array.isArray(changes) && changes.length > 0;
          return (
            <div key={e.id}>
              <button
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm',
                  expandable && 'hover:bg-accent/50',
                )}
                onClick={() => expandable && setExpanded(isOpen ? null : e.id)}
                disabled={!expandable}
              >
                {expandable ? (
                  isOpen ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )
                ) : (
                  <span className="w-3.5 shrink-0" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{buildDescription(e)}</span>
                  {summary && (
                    <span className="block truncate text-xs text-muted-foreground">Changed: {summary}</span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground" title={fullTime(e.created_at)}>
                  {relativeTime(e.created_at)}
                </span>
              </button>
              {isOpen && Array.isArray(changes) && (
                <div className="border-t bg-muted/30 px-9 py-2">
                  <table className="w-full text-xs">
                    <tbody>
                      {changes.map((c, i) => (
                        <tr key={i} className="border-b border-border/40 last:border-0">
                          <td className="w-32 py-1.5 pr-3 font-medium">{c.label}</td>
                          {c.action ? (
                            <td
                              className={cn(
                                'py-1.5',
                                c.action === 'added' && 'text-emerald-600 dark:text-emerald-400',
                                c.action === 'removed' && 'text-red-600 dark:text-red-400',
                                c.action === 'changed' && 'text-amber-600 dark:text-amber-400',
                              )}
                            >
                              {c.action}
                            </td>
                          ) : (
                            <td className="py-1.5">
                              <span className="text-muted-foreground line-through">{c.from ?? '—'}</span>
                              <span className="mx-2 text-muted-foreground">→</span>
                              <span>{c.to ?? '—'}</span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {data && data.entries.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">No audit entries match.</p>
        )}
      </div>
      {data && data.pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Page {data.page} of {data.pages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.page <= 1}
              onClick={() => setPage(String(data.page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.page >= data.pages}
              onClick={() => setPage(String(data.page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
